/**
 * client/src/pages/Structure.tsx — fix a book the detector got wrong, in under thirty seconds.
 *
 * P1 shipped this screen read-only and promised the rest here. What it had already was the half that
 * has to exist first — **the evidence** — because §3.4 is explicit that the override is the actual
 * answer and that the runner-up candidates must be shown *with their scores*: a gate that shows a
 * verdict and hides the reasoning gives a human nothing to disagree with. That is now
 * `structure/EvidenceRail.tsx`, and this file is the four verbs around it.
 *
 * ── The shape of the screen, and why ────────────────────────────────────────────────────────────
 *
 *   1. the decision, with every signal scored and every loser priced   (EvidenceRail)
 *   2. what this book's shape suggests, as offers                      (Suggestions)
 *   3. the chapters, editable                                          (ChapterList / BlockMode)
 *   4. what the import threw away                                      (Report)
 *
 * and, whenever a merge or a split is staged, a bar at the bottom that says what it will cost before
 * anything is written:
 *
 *      14 chapters change · 0 GPU · re-stitch 41 pages
 *
 * ⚠️ **That sentence is not computed here.** It comes back from `POST /editions/:id/restructure`
 * with `dryRun: true`, which runs the identical code path as the commit and stops before the write.
 * A cost estimated by the client and a cost paid by the server are two implementations of one rule,
 * and the case they disagree on is the one nobody screenshots.
 *
 * ⚠️ **Why merges are staged and roles are not.** Renumbering is the expensive operation in this
 * product: a merge changes `chapterOrd` for everything after it, which changes every `sid`, which
 * means a new edition (§2.1 — editions are immutable) and a `doc_remaps` migration of every stored
 * position. Doing that five times to fix five chapters is five renumbers and five sets of expired
 * page artifacts. Marking a chapter front matter moves nothing and is written immediately.
 */

import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AppFrame, Blank, IconAlert, IconBack, IconCheck } from "../App.tsx";
import { ApiError, abApi, api } from "../lib/api.ts";
import type { BookDetail, DroppedRow, IngestReport, ReportCount } from "../lib/api.ts";
import { useCachedQuery } from "../lib/idb.ts";
import { BlockMode } from "../structure/BlockMode.tsx";
import { ChapterList } from "../structure/ChapterList.tsx";
import type { EditableStructure, RestructureOp, StructureSuggestion } from "../structure/ChapterList.tsx";
import { EvidenceRail } from "../structure/EvidenceRail.tsx";

const COUNT_LABELS: Record<ReportCount, string> = {
  chapters: "Chapters",
  blocks: "Blocks",
  sentences: "Sentences",
  words: "Words",
  chars: "Characters",
  pages: "Pages",
  images: "Images",
  notes: "Footnotes",
  tables: "Tables",
  verse: "Verse lines",
};

/** `POST /editions/:id/restructure` — the same object whether it was a dry run or the real thing. */
interface RestructureCost {
  chaptersBefore: number;
  chaptersAfter: number;
  chaptersRenumbered: number;
  sentencesRenumbered: number;
  pagesBefore: number;
  pagesAfter: number;
  pagesToRestitch: number;
  clipsCarried: number;
  gpuSecAdded: number;
  gpuReason: string;
  positionsCarried: number;
  annotationsCarried: number;
}
interface RestructureAnswer {
  dryRun: boolean;
  editionId: string;
  cost: RestructureCost;
  chapters: { id: string; title: string; renumbered: boolean; fromChapterIds: string[] }[];
}

export function Structure() {
  const { bookId = "" } = useParams<{ bookId: string }>();
  const navigate = useNavigate();

  const detail = useCachedQuery<BookDetail>(`book:${bookId}`, () => abApi.book(bookId));
  const editionId = detail.data?.edition?.id ?? "";

  // Both reads are keyed on the edition, never on the book: an edition is immutable, so a cached
  // report can never be stale for the id it was fetched under (LAW 1's practical payoff). A
  // restructure mints a NEW edition id, so the cache cannot survive one either — which is exactly
  // right, because the thing it cached no longer describes the book.
  const report = useCachedQuery<IngestReport | null>(`report:${editionId}`, () =>
    editionId ? abApi.report(editionId) : Promise.resolve(null),
  );
  const structure = useCachedQuery<EditableStructure | null>(`structure:${editionId}`, () =>
    editionId ? api.get<EditableStructure>(`/editions/${encodeURIComponent(editionId)}/structure`) : Promise.resolve(null),
  );

  const [ops, setOps] = useState<RestructureOp[]>([]);
  const [quote, setQuote] = useState<RestructureAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<{ pages: string[] } | null>(null);

  const view = structure.data ?? null;

  const reloadStructure = structure.reload;

  /**
   * Price a staged set of ops. Runs on every change to the list, and the answer is the one the commit
   * will use — see the header. A failure here is shown rather than swallowed: the most useful case is
   * the server refusing an op ("the first chapter has nothing before it to merge into"), and that is
   * a sentence the human needs *before* they press Apply, not after.
   */
  const price = useCallback(
    async (next: RestructureOp[]) => {
      if (next.length === 0 || editionId === "") {
        setQuote(null);
        setFailure(null);
        return;
      }
      try {
        const answer = await api.post<RestructureAnswer>(`/editions/${encodeURIComponent(editionId)}/restructure`, {
          ops: next,
          dryRun: true,
        });
        setQuote(answer);
        setFailure(null);
      } catch (err) {
        setQuote(null);
        setFailure(err instanceof ApiError ? err.message : String(err));
      }
    },
    [editionId],
  );

  const stage = useCallback(
    (added: RestructureOp[]) => {
      setOps((current) => {
        const next = [...current, ...added.filter((a) => !current.some((c) => same(c, a)))];
        void price(next);
        return next;
      });
    },
    [price],
  );

  const unstage = useCallback(
    (predicate: (op: RestructureOp) => boolean) => {
      setOps((current) => {
        const next = current.filter((o) => !predicate(o));
        void price(next);
        return next;
      });
    },
    [price],
  );

  /** An immediate write — role, speak, title, where the book begins. Nothing here moves a sid. */
  const patch = useCallback(
    async (body: Record<string, unknown>) => {
      if (editionId === "") return;
      setBusy(true);
      setFailure(null);
      try {
        await api.patch(`/editions/${encodeURIComponent(editionId)}/structure`, body);
        reloadStructure();
      } catch (err) {
        setFailure(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [editionId, reloadStructure],
  );

  const apply = useCallback(async () => {
    if (editionId === "" || ops.length === 0) return;
    setBusy(true);
    setFailure(null);
    try {
      await api.post<RestructureAnswer>(`/editions/${encodeURIComponent(editionId)}/restructure`, { ops });
      setOps([]);
      setQuote(null);
      // The book now points at a NEW edition, so the book detail is what has to be re-read; the
      // structure query is keyed on the edition id and re-runs by itself when that changes.
      detail.reload();
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [editionId, ops, detail]);

  const confirm = useCallback(async () => {
    if (editionId === "") return;
    setBusy(true);
    setFailure(null);
    try {
      // LAW 10 lives on the far side of this call: confirming warms the first three body pages
      // unconditionally, so the first press of play is instant rather than a twenty-second stare.
      const answer = await api.post<{ warmed?: { pageIds?: string[] } }>(
        `/editions/${encodeURIComponent(editionId)}/confirm`,
        {},
      );
      setConfirmed({ pages: answer.warmed?.pageIds ?? [] });
      reloadStructure();
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [editionId, reloadStructure]);

  const suggestions = useMemo(
    () => (view?.suggestions ?? []).filter((s) => !s.ops.every((o) => ops.some((c) => same(c, o)))),
    [view, ops],
  );

  if (detail.loading && !detail.data) return <AppFrame><Blank /></AppFrame>;

  if (!detail.data) {
    const missing = detail.error instanceof ApiError && detail.error.notFound;
    return (
      <AppFrame>
        <div className="ab-empty">
          <h2>{missing ? "No book here." : "Couldn't open that book."}</h2>
          <p>{missing ? "It was deleted, or it isn't yours." : (detail.error?.message ?? "")}</p>
          <Link to="/" className="ab-btn ab-btn-primary">
            Back to the shelf
          </Link>
        </div>
      </AppFrame>
    );
  }

  const { book, edition } = detail.data;

  return (
    <AppFrame>
      <button type="button" className="ab-back" onClick={() => navigate(`/b/${bookId}`)}>
        <IconBack />
        <span>{book.title}</span>
      </button>

      <h1 className="ab-h1">Where this book begins</h1>
      <p className="ab-lede">
        Narrating twelve minutes of publisher boilerplate is infuriating and costs GPU, so the
        detector is deliberately biased <em>late</em>: it would rather start two paragraphs into
        chapter one than read you the copyright page. Every signal it weighed is below, and every one
        of them is yours to overrule.
      </p>

      {!edition && (
        <p className="ab-note ab-note-warn">
          <IconAlert />
          <span>This book has no imported edition yet. Come back when the import finishes.</span>
        </p>
      )}

      {edition && (
        <>
          <EvidenceRail
            view={view}
            loading={structure.loading}
            error={structure.error}
            fallback={edition}
            onConfirm={() => void confirm()}
            confirming={busy}
            // Only when nothing is staged: with a merge in the tray the same string belongs beside
            // the Apply button, and one failure shown twice reads as two failures.
            confirmError={ops.length === 0 ? failure : null}
          />

          {confirmed !== null && (
            <p className="ab-note">
              <IconCheck />
              <span>
                Confirmed.{" "}
                {confirmed.pages.length > 0
                  ? `The first ${confirmed.pages.length} body ${confirmed.pages.length === 1 ? "page is" : "pages are"} being narrated now, so the first press of play is instant.`
                  : "Narration is switched off on this box, so nothing was warmed."}
              </span>
            </p>
          )}

          {view !== null && suggestions.length > 0 && <Suggestions suggestions={suggestions} onStage={stage} />}

          {view !== null &&
            (view.blockMode ? (
              <BlockMode
                editionId={view.editionId}
                chapterCount={view.chapters.length}
                ops={ops}
                onStage={stage}
                onUnstage={unstage}
                onPatch={patch}
                busy={busy}
              />
            ) : (
              <ChapterList view={view} ops={ops} onStage={stage} onUnstage={unstage} onPatch={patch} busy={busy} />
            ))}

          <Report data={report.data ?? null} loading={report.loading} error={report.error} />
        </>
      )}

      {failure !== null && ops.length === 0 && <p className="ab-hint ab-hint-bad">{failure}</p>}

      {ops.length > 0 && (
        <PriceTag
          ops={ops}
          quote={quote}
          failure={failure}
          busy={busy}
          onApply={() => void apply()}
          onClear={() => {
            setOps([]);
            setQuote(null);
            setFailure(null);
          }}
        />
      )}
    </AppFrame>
  );
}

const same = (a: RestructureOp, b: RestructureOp): boolean =>
  a.op === "merge" && b.op === "merge"
    ? a.chapterId === b.chapterId
    : a.op === "split" && b.op === "split"
      ? a.blockId === b.blockId
      : false;

// ─── what the book's shape suggests ──────────────────────────────────────────

/**
 * The repairs P1 found and deliberately refused to make on its own.
 *
 * Pride and Prejudice chapterises to 72 chapters for a 61-chapter novel, and P1-STATUS records why it
 * was left alone: merging adjacent chapters with equal titles "is a heuristic over chapter *identity*,
 * and chapter ords are baked into every sid in the edition (LAW 1). Getting it wrong renumbers a
 * book's coordinate system. It belongs with the structure editor in P3, where a human confirms the
 * merge." This is that confirmation, and the reason these are *buttons* and not a migration.
 */
function Suggestions({ suggestions, onStage }: { suggestions: StructureSuggestion[]; onStage: (ops: RestructureOp[]) => void }) {
  return (
    <section className="ab-panel">
      <h2 className="ab-h2">This looks like a book the parser split badly</h2>
      <ul className="ab-runners">
        {suggestions.map((s) => (
          <li key={s.id} style={{ display: "block" }}>
            <p className="ab-runner-title">{s.title}</p>
            <p className="ab-hint">{s.detail}</p>
            <div className="ab-actions">
              <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm ab-btn-wrap" onClick={() => onStage(s.ops)}>
                Stage {s.ops.length === 1 ? "this change" : `these ${s.ops.length} changes`}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <p className="ab-hint ab-hint-quiet">
        Nothing here is applied until you press Apply, and you see what it costs first.
      </p>
    </section>
  );
}

// ─── the price ───────────────────────────────────────────────────────────────

/**
 * *"14 chapters change · 0 GPU · re-stitch 41 pages."*
 *
 * The three numbers are the three currencies a restructure spends. The middle one is the important
 * one and it is genuinely zero: `clipKey` is position-free by construction (LAW 7), so every second
 * of GPU already spent on this book is found again under the new ids, including the takes somebody
 * regenerated by hand. The third is CPU on the `media` lane — a lossless concat of clips that already
 * exist plus one encode, per page that currently holds audio.
 */
function PriceTag({
  ops,
  quote,
  failure,
  busy,
  onApply,
  onClear,
}: {
  ops: RestructureOp[];
  quote: RestructureAnswer | null;
  failure: string | null;
  busy: boolean;
  onApply: () => void;
  onClear: () => void;
}) {
  const cost = quote?.cost ?? null;
  return (
    <section className="ab-panel" aria-live="polite">
      <h2 className="ab-h2">
        {ops.length} change{ops.length === 1 ? "" : "s"} staged
      </h2>

      {failure !== null && (
        <p className="ab-note ab-note-warn">
          <IconAlert />
          <span>{failure}</span>
        </p>
      )}

      {cost !== null && (
        <>
          <p className="ab-decision-title">
            {cost.chaptersRenumbered} chapter{cost.chaptersRenumbered === 1 ? "" : "s"} change ·{" "}
            {cost.gpuSecAdded === 0 ? "0 GPU" : `${cost.gpuSecAdded}s GPU`} ·{" "}
            {cost.pagesToRestitch === 0
              ? "nothing to re-stitch"
              : `re-stitch ${cost.pagesToRestitch} page${cost.pagesToRestitch === 1 ? "" : "s"}`}
          </p>
          <dl className="ab-facts">
            <div>
              <dt>Chapters</dt>
              <dd>
                {cost.chaptersBefore} → {cost.chaptersAfter}
              </dd>
            </div>
            <div>
              <dt>Sentences renumbered</dt>
              <dd>{cost.sentencesRenumbered.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Clips kept</dt>
              <dd>{cost.clipsCarried.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Places carried over</dt>
              <dd>{(cost.positionsCarried + cost.annotationsCarried).toLocaleString()}</dd>
            </div>
          </dl>
          <p className="ab-hint">{cost.gpuReason}</p>
          <p className="ab-hint ab-hint-quiet">
            This makes a new edition of the book — the old one is kept, and every bookmark and saved
            position is carried across it. Nothing is deleted and no text changes.
          </p>
        </>
      )}

      {cost === null && failure === null && <p className="ab-hint">Working out what this costs…</p>}

      <div className="ab-actions">
        <button
          type="button"
          className="ab-btn ab-btn-primary"
          disabled={busy || cost === null}
          onClick={onApply}
        >
          {busy ? "Applying…" : "Apply"}
        </button>
        <button type="button" className="ab-btn ab-btn-ghost" disabled={busy} onClick={onClear}>
          Cancel all
        </button>
      </div>
    </section>
  );
}

// ─── the report ──────────────────────────────────────────────────────────────

/**
 * `report.dropped` may arrive as rows or as a `{category: count}` map — whichever
 * `worker/src/ingest/report.ts` finds natural. Normalising here costs eight lines; a screen that
 * throws over the shape of a *report about what went wrong* would be its own joke.
 */
function droppedRows(dropped: IngestReport["dropped"]): DroppedRow[] {
  if (!dropped) return [];
  if (Array.isArray(dropped)) return dropped.filter((d) => d && typeof d.count === "number");
  return Object.entries(dropped)
    .filter(([, count]) => typeof count === "number" && count > 0)
    .map(([what, count]) => ({ what, count }));
}

function Report({ data, loading, error }: { data: IngestReport | null; loading: boolean; error: Error | null }) {
  if (loading && !data) {
    return (
      <section className="ab-panel">
        <h2 className="ab-h2">What was found</h2>
        <p className="ab-hint">Loading the report…</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="ab-panel">
        <h2 className="ab-h2">What was found</h2>
        <p className="ab-hint ab-hint-bad">{error?.message ?? "No report was written for this edition."}</p>
      </section>
    );
  }

  const counts = Object.entries(data.counts ?? {}).filter(([, v]) => typeof v === "number") as [ReportCount, number][];
  const dropped = droppedRows(data.dropped);

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">What was found</h2>

      {counts.length > 0 && (
        <dl className="ab-facts">
          {counts.map(([k, v]) => (
            <div key={k}>
              <dt>{COUNT_LABELS[k] ?? k}</dt>
              <dd>{v.toLocaleString()}</dd>
            </div>
          ))}
        </dl>
      )}

      <h3 className="ab-h3">What was left out of the reading, and why</h3>
      {dropped.length === 0 ? (
        <p className="ab-hint">Nothing was left out.</p>
      ) : (
        <table className="ab-table">
          <thead>
            <tr>
              <th>What</th>
              <th className="ab-num-col">Count</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {dropped.map((d, i) => (
              <tr key={`${d.what}-${i}`}>
                <td>{d.what}</td>
                <td className="ab-num-col ab-num" data-label="Count">
                  {d.count.toLocaleString()}
                </td>
                <td className="ab-table-detail" data-label="Why">
                  {d.why ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="ab-hint">
        Kept but not spoken by default: the copyright page, the dedication, the epigraph, the table of
        contents, the index and every footnote. Only running heads and page folios are genuinely
        removed, and the folios survive as provenance so a citation still works.
      </p>

      {data.warnings && data.warnings.length > 0 && (
        <>
          <h3 className="ab-h3">Warnings</h3>
          <ul className="ab-warnings">
            {data.warnings.map((w, i) => (
              <li key={i}>
                <IconAlert />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {data.source && (
        <p className="ab-hint ab-hint-quiet">
          source {data.source.ext.replace(/^\./, "").toUpperCase()}
          {typeof data.ms === "number" && ` · imported in ${(data.ms / 1000).toFixed(1)}s`}
        </p>
      )}
    </section>
  );
}
